import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import type { StatusBookingRequestWebhookError } from '@anyroad/api';
import type {
  CheckoutContextValue,
  PersonalInfoFormData,
  GuestInfoFormData,
  AdditionalInfoFormData,
  PricingData,
  SummaryProps,
  UnitConfiguration,
  AddonConfiguration,
  PartnerInfo,
  KioskSetting,
  StripeState,
  SegmentTrackedPages,
} from './types';
import { getNextSegmentTrackedPages } from './segmentTrackingHelpers';

const CheckoutContext = createContext<CheckoutContextValue | undefined>(
  undefined
);

interface CheckoutProviderProps {
  children: ReactNode;

  flags?: Record<string, any>;

  bookingRequestId: string;
  tourId?: number;
  units: UnitConfiguration[];
  selectedAddons: AddonConfiguration[];

  pricingData: PricingData;
  summaryProps: SummaryProps;

  includesTransport: boolean;

  total: number;
  currency: string;
  zeroPricedTour: boolean | null;
  fullyDiscounted: boolean;
  paidInCash: boolean;

  isBookingInvoice: boolean;
  isLiveBooking: boolean;
  isAffiliate: boolean;
  isUnpaidBooking: boolean;
  isPayLaterEnabled: boolean;
  isOutsidePayLaterCutoff: boolean;
  isRsvpEnabled: boolean;

  phoneMandatory: boolean;
  isGuestInfoCollectionEnabled: boolean;
  isGuestOfGuestEnabled: boolean;
  guestOfGuestMaxGuests: number;

  partnerInfo: PartnerInfo;
  kioskSetting?: KioskSetting;
  managedAccountToken?: string;
  stripeTheme?: any;

  tourSettings?: any;

  i18nLang: string;
  i18nList: string[];

  willExpireOn?: string;
  showTranslate: boolean;

  privacyPolicyLink: string;
  privacyPolicyLinkText: string;
  hidePolicyLinks: boolean;

  discountApplied: boolean;
  discountLoading: boolean;
  discountErrors?: any;
  isGA4Enabled: boolean;
  loading: boolean;
  isMobileFirstCheckoutWebhooksEnabled: boolean;

  webhookError?: StatusBookingRequestWebhookError | null;
  startPolling?: () => void;
  /** When provided (e.g. by CheckoutContainer), context uses this as single source of truth for page 1 form data */
  pageOneData?: Partial<
    PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
  > | null;
  setPageOneData?: (
    data: Partial<
      PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
    >
  ) => void;
}

export const CheckoutProvider: React.FC<CheckoutProviderProps> = ({
  children,
  flags,
  bookingRequestId,
  tourId,
  units,
  selectedAddons,
  pricingData,
  summaryProps,
  includesTransport,
  total,
  currency,
  zeroPricedTour,
  fullyDiscounted,
  paidInCash,
  isBookingInvoice,
  isLiveBooking,
  isAffiliate,
  isUnpaidBooking,
  isPayLaterEnabled,
  isOutsidePayLaterCutoff,
  isRsvpEnabled,
  phoneMandatory,
  isGuestInfoCollectionEnabled,
  isGuestOfGuestEnabled,
  guestOfGuestMaxGuests,
  partnerInfo,
  kioskSetting,
  managedAccountToken,
  stripeTheme,
  tourSettings,
  i18nLang,
  i18nList,
  willExpireOn,
  showTranslate,
  privacyPolicyLink,
  privacyPolicyLinkText,
  hidePolicyLinks,
  discountApplied,
  discountLoading,
  discountErrors,
  isGA4Enabled,
  loading,
  isMobileFirstCheckoutWebhooksEnabled,
  webhookError,
  startPolling,
  pageOneData: pageOneDataProp,
  setPageOneData: setPageOneDataProp,
}) => {
  const [currentPage, setCurrentPage] = useState<1 | 2>(1);
  const [pageOneDataInternal, setPageOneDataInternal] = useState<Partial<
    PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
  > | null>(null);
  const pageOneData = pageOneDataProp ?? pageOneDataInternal;
  const setPageOneData = setPageOneDataProp ?? setPageOneDataInternal;
  const [bookerUnitId, setBookerUnitId] = useState<number | null>(null);
  const [stripeState, setStripeState] = useState<StripeState>({
    fieldsCompleted: [],
  });
  const [segmentTrackedPages, setSegmentTrackedPages] =
    useState<SegmentTrackedPages>({
      pageOne: false,
      pageTwo: false,
    });

  useEffect(() => {
    setSegmentTrackedPages({
      pageOne: false,
      pageTwo: false,
    });
  }, [bookingRequestId]);

  const markSegmentTrackedPage = useCallback((page: 1 | 2) => {
    setSegmentTrackedPages((prev) => getNextSegmentTrackedPages(prev, page));
  }, []);

  const value = useMemo<CheckoutContextValue>(
    () => ({
      currentPage,
      setCurrentPage,
      pageOneData,
      setPageOneData,

      flags,

      bookingRequestId,
      tourId,
      units,
      selectedAddons,

      pricingData,
      summaryProps,

      includesTransport,

      total,
      currency,
      zeroPricedTour,
      fullyDiscounted,
      paidInCash,

      isBookingInvoice,
      isLiveBooking,
      isAffiliate,
      isUnpaidBooking,
      isPayLaterEnabled,
      isOutsidePayLaterCutoff,
      isRsvpEnabled,

      phoneMandatory,
      isGuestInfoCollectionEnabled,
      isGuestOfGuestEnabled,
      guestOfGuestMaxGuests,

      partnerInfo,
      kioskSetting,
      managedAccountToken,
      stripeTheme,

      tourSettings,

      i18nLang,
      i18nList,

      willExpireOn,
      showTranslate,

      privacyPolicyLink,
      privacyPolicyLinkText,
      hidePolicyLinks,

      bookerUnitId,
      setBookerUnitId,

      stripeState,
      setStripeState,

      discountApplied,
      discountLoading,
      discountErrors,

      isGA4Enabled,
      loading,
      isMobileFirstCheckoutWebhooksEnabled,
      webhookError,
      startPolling,
      segmentTrackedPages,
      markSegmentTrackedPage,
    }),
    [
      currentPage,
      pageOneData,
      setPageOneData,
      flags,
      bookingRequestId,
      tourId,
      units,
      selectedAddons,
      pricingData,
      summaryProps,
      includesTransport,
      total,
      currency,
      zeroPricedTour,
      fullyDiscounted,
      paidInCash,
      isBookingInvoice,
      isLiveBooking,
      isAffiliate,
      isUnpaidBooking,
      isPayLaterEnabled,
      isOutsidePayLaterCutoff,
      isRsvpEnabled,
      phoneMandatory,
      isGuestInfoCollectionEnabled,
      isGuestOfGuestEnabled,
      guestOfGuestMaxGuests,
      partnerInfo,
      kioskSetting,
      managedAccountToken,
      stripeTheme,
      tourSettings,
      i18nLang,
      i18nList,
      willExpireOn,
      showTranslate,
      privacyPolicyLink,
      privacyPolicyLinkText,
      hidePolicyLinks,
      bookerUnitId,
      stripeState,
      discountApplied,
      discountLoading,
      discountErrors,
      isGA4Enabled,
      loading,
      isMobileFirstCheckoutWebhooksEnabled,
      webhookError,
      startPolling,
      segmentTrackedPages,
      markSegmentTrackedPage,
    ]
  );

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = (): CheckoutContextValue => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};
